const axios = require('axios');
const { gql, request } = require('graphql-request');
const BLS = require('@chainsafe/bls/herumi');
const { decrypt, Keystore } = require('@chainsafe/bls-keystore');
const { ethers } = require('ethers');
const {
    _extractChainID,
    _extractBlockData,
    customErrors,
    cipStatuses,
    _getChainSpecificConstants,
    ZERO
} = require('./constants');
const { _remove0x, _add0x, _getStakehouse, _getSLOTBalanceInVault } = require('./utils');
const { getContractInstance } = require('./contracts');
const _ = require('lodash');

const BASE_CRYPTOGRAPHY_WRAPPER_URL = 'https://esmqkaymqb.execute-api.eu-central-1.amazonaws.com/dev';
const SLOT_REQUEST_THRESHOLD = ethers.utils.parseEther('2');

const _generateAESCredentials = async () => {
    const generateCredentialsURL = `${BASE_CRYPTOGRAPHY_WRAPPER_URL}/generateAesCredentials`;
    const resp = await axios.get(generateCredentialsURL);

    return resp.data;
};

const _formAESKeystore = async (aesPrivateKey, password) => {
    const formAESKeystoreURL = `${BASE_CRYPTOGRAPHY_WRAPPER_URL}/formAESKeystore`;

    const resp = await axios.post(
        formAESKeystoreURL,
        {
            aesPrivateKey: _remove0x(aesPrivateKey).toUpperCase(),
            password
        }
    );

    return resp.data;
}

const _unlockBLSKeystore = async (keystore, password) => {
    await BLS.init();
    const { bls } = BLS;

    const decryptedSigningKey = await decrypt(keystore, password);

    let publicKey = bls.secretKeyToPublicKey(decryptedSigningKey);

    publicKey = Buffer.from(publicKey).toString('hex');
    const privateKey = Buffer.from(decryptedSigningKey).toString('hex');

    return { publicKey, privateKey };
};

const _validateBLSKeystore = async (keystore, password, blsPublicKey) => {

    const { publicKey } = await _unlockBLSKeystore(keystore, password);

    return _remove0x(publicKey).toLowerCase() === _remove0x(blsPublicKey).toLowerCase();
};

const _unlockAESPrivateKey = async (signer, keystore, password, blsPublicKey) => {

    let aesPrivateKey;

    try {
        const decryptAESPrivateKeyURL = `${BASE_CRYPTOGRAPHY_WRAPPER_URL}/unlockAESPrivateKey`;
        const response = await axios.post(
            decryptAESPrivateKeyURL,
            {
                keystore,
                password
            }
        );

        aesPrivateKey = response.data;
    }
    catch (e) {
        throw new Error(customErrors.INCORRECT_KEYSTORE_PASSWORD);
    }

    const aesPublicKey = await _getAESPublicKeyfromPrivateKey(aesPrivateKey);
    const decryptionRequests = await _getDecryptionRequests(
        signer,
        _add0x(aesPublicKey).toLowerCase(),
        _add0x(blsPublicKey).toLowerCase()
    );

    if (!decryptionRequests) {
        throw new Error(customErrors.INCORRECT_AES_KEYSTORE);
    }

    return aesPrivateKey;
};

const _formCIPCiphertext = async (signer, blsPrivateKey) => {

    const chainID = await _extractChainID(signer);
    const { cipConstants } = _getChainSpecificConstants(chainID);

    const formCIPCiphertextURL = `${BASE_CRYPTOGRAPHY_WRAPPER_URL}/formCIPCiphertext`;
    const response = await axios.post(
        formCIPCiphertextURL,
        {
            blsPrivateKey,
            dkgPublicKey: cipConstants.DKG_PUBLIC_KEY
        }
    );

    return response.data;
};

const _isReEncryptionCompleted = async (signer, blsPublicKey) => {

    blsPublicKey = _add0x(blsPublicKey);

    const contract = (await getContractInstance(signer)).safeBox();

    return contract.reEncryptionTracker(
        blsPublicKey
    );
};

const _reEncrypt = async (signer, blsPublicKey, stakehouseAddress, blsKeystore, blsKeystorePassword) => {

    const contract = (await getContractInstance(signer)).safeBox();

    const derivedKeys = await _unlockBLSKeystore(
        blsKeystore,
        blsKeystorePassword
    );

    const { ciphertext, aesEncryptorKey } = await _formCIPCiphertext(signer, derivedKeys.privateKey);

    return contract.reEncryptSigningKey(
        _add0x(blsPublicKey),
        stakehouseAddress,
        Buffer.from(ciphertext),
        Buffer.from(aesEncryptorKey, 'hex')
    );
};

const _getSafeBox = async signer =>
    (await getContractInstance(signer)).safeBox()

const _decryptionEligibilityChecks = async (signer, blsPublicKey, stakehouseAddress=null, safeBox=null) => {

    blsPublicKey = _add0x(blsPublicKey);
    safeBox = safeBox ? safeBox : await _getSafeBox(signer);
    stakehouseAddress = stakehouseAddress ? stakehouseAddress : await _getStakehouse(signer, blsPublicKey);

    const isActiveDecryptionRequestPresent = await safeBox.isDecryptionRequestValid(blsPublicKey);
    if(isActiveDecryptionRequestPresent) {
        throw new Error(customErrors.DECRYPTION_REQUEST_EXISTS);
    }

    const slotRegistry = (await getContractInstance(signer)).slotSettlementRegistry();
    const slashedAmount = await slotRegistry.currentSlashedAmountForKnot(blsPublicKey);

    if(slashedAmount.gt(ZERO)) {
        throw new Error(customErrors.SLASHED_KNOT);
    }

    const userAddress = await signer.getAddress();
    const collateralizedSlotAmount = await _getSLOTBalanceInVault(
        signer,
        stakehouseAddress,
        userAddress,
        blsPublicKey
    );

    if (collateralizedSlotAmount.lte(SLOT_REQUEST_THRESHOLD)) {
        throw new Error(customErrors.SLOT_BALANCE_LT_2);
    }

    return true
};

const _applyForDecryption = async (signer, blsPublicKey, stakehouseAddress, aesPublicKey) => {

    const safeBox = await _getSafeBox(signer);
    await _decryptionEligibilityChecks(signer, blsPublicKey, stakehouseAddress, safeBox);

    return safeBox.applyForDecryption(
        _add0x(blsPublicKey),
        stakehouseAddress,
        Buffer.from(_remove0x(aesPublicKey), 'hex')
    );
};

const _getRequestAvailabilityBlock = async (signer, blsPublicKey) => {

    const adjustedBlsPublicKey = _add0x(blsPublicKey);

    const contract = (await getContractInstance(signer)).safeBox();

    // value is true if, a decryption request is already present.
    const isActiveDecryptionRequestPresent = await contract.isDecryptionRequestValid(adjustedBlsPublicKey);

    if(!isActiveDecryptionRequestPresent) {
        // If the request is not present then there is no delay
        return 0;
    }

    const validityPeriod = await contract.decryptionValidityPeriod();
    const knotIDToLastRequest = await contract.knotIdToLastRequest(adjustedBlsPublicKey);
    let currentBlock = (await _extractBlockData()).number;
    currentBlock = ethers.BigNumber.from(currentBlock);

    const block = validityPeriod.add(knotIDToLastRequest.sub(currentBlock));

    return block.gt(ethers.BigNumber.from('0')) ? block : 0;
};

const _getPartialDecryptionPieces = async (signer, blsPublicKey, aesPublicKey) => {

    const chainID = await _extractChainID(signer);
    const { cipConstants } = _getChainSpecificConstants(chainID);

    const lookupQuery = gql`
		query getDecryptionPieces($blsPublicKey: Bytes!) {
			decryptionPieces (where: {blsPublicKey: $blsPublicKey}) {
                guardian
                recipientAesKey
                blsPublicKey
                ciphertext
                zkProof
                nonce
            }
		}
	`;

	const response = await request(
		cipConstants.CIP_SUBGRAPH_ENDPOINT,
		lookupQuery,
		{
			blsPublicKey: _add0x(blsPublicKey.toLowerCase())
		}
	);

	if (!response || !response.decryptionPieces) {
		throw new Error('Invalid response getting the decryption pieces');
	}

    /// Get most recent request matching the aes public key
    const decryptionPieces = response.decryptionPieces.filter(piece => piece.recipientAesKey === aesPublicKey);
    const maxNonce = _.maxBy(decryptionPieces, piece => Number(piece.nonce)).nonce;

    return decryptionPieces.filter(piece => piece.nonce === maxNonce);
};

const _getAllGuardians = async (signer) => {

    const chainID = await _extractChainID(signer);
    const { cipConstants } = _getChainSpecificConstants(chainID);

    const lookupQuery = gql`
		query getGuardians {
            guardians {
                id
                aesPublicKey
                guardianIndexPointer
                sharedPublicKey
            }
		}
	`;

	const response = await request(
		cipConstants.CIP_SUBGRAPH_ENDPOINT,
		lookupQuery
	);

	if (!response || !response.guardians) {
		throw new Error('Invalid response getting the DKG Results');
	}

    return _.fromPairs(response.guardians.map(obj => [obj.id, _.omit(obj, 'id')]));
};

const _getThreshold = async (signer) => {
    const contract = (await getContractInstance(signer)).safeBox();

    return contract.threshold();
};

const _getAESPublicKeyfromPrivateKey = async aesPrivateKey => {
    const url = `${BASE_CRYPTOGRAPHY_WRAPPER_URL}/getAESPublicKeyFromPrivateKey`;
    const resp = await axios.post(url, { aesPrivateKey });

    return resp.data;
}

const _getDecryptionRequests = async (signer, aesPublicKey, blsPublicKey) => {

    const chainID = await _extractChainID(signer);
    const { cipConstants } = _getChainSpecificConstants(chainID);

    const lookupQuery = gql`
        query getDecryptionRequests($recipientAesKey: Bytes! $blsPublicKey: Bytes!) {
            decryptionRequests(orderBy: blockNumber, orderDirection: asc
                where: { recipientAesKey: $recipientAesKey blsPublicKey: $blsPublicKey }
            ) {
                id
                totalNumberOfPiecesReceived
                blsPublicKey
                nonce
                recipientAesKey
            }
        }
    `;

    const response = await request(
		cipConstants.CIP_SUBGRAPH_ENDPOINT,
		lookupQuery,
        {
            recipientAesKey: aesPublicKey,
            blsPublicKey: blsPublicKey
        }
    );

    if (!response || !response.decryptionRequests) {
        throw new Error('Failed to fetch decryption requests')
    }

    return _.maxBy(response.decryptionRequests, req => Number(req.nonce));
};

const _getInitialEncryption = async (signer, id) => {

    const chainID = await _extractChainID(signer);
    const { cipConstants } = _getChainSpecificConstants(chainID);

    const lookupQuery = gql`
        query getEncryptions($id: Bytes!) {
            encryptions (where: { id: $id }) {
                id
                ciphertext
                encryptorAesKey
            }
        }
    `;

    const response = await request(
        cipConstants.CIP_SUBGRAPH_ENDPOINT,
        lookupQuery,
        { id: id }
    );

    if (!response || !response.encryptions) {
        throw new Error('Failed to fetch encryption result');
    }

    return response.encryptions[0];
};

const _decryptPiece = async (piece, guardianMap, privateKey, initialEncryption) => {

    const { guardian } = piece;

    if (!guardianMap[guardian]) {
        throw customErrors.INVALID_GUARDIAN;
    }

    const { aesPublicKey } = guardianMap[guardian];

    if (!aesPublicKey) {
        throw customErrors.INVALID_AES_PUBLIC_KEY;
    }

    try {
        const decryptCiphertextUrl = `${BASE_CRYPTOGRAPHY_WRAPPER_URL}/decryptCipherText`;
        const plaintextResp = await axios.post(
            decryptCiphertextUrl,
            {
                ciphertext: [...Buffer.from(piece.ciphertext.slice(2), 'hex')],
                aesPublicKey: guardianMap[guardian].aesPublicKey.slice(2).toUpperCase(),
                aesPrivateKey: privateKey
            }
        );
        const plaintext = plaintextResp.data;

        const proofUrl = `${BASE_CRYPTOGRAPHY_WRAPPER_URL}/verifyNIZKProof`;

        const proofResp = await axios.post(
            proofUrl,
            {
                sharedPublicKey: guardianMap[guardian].sharedPublicKey.slice(2).toUpperCase(),
                encryptorAesKey: initialEncryption.encryptorAesKey.slice(2).toUpperCase(),
                plaintext: [...Buffer.from(plaintext, 'hex')],
                zkProof: [...Buffer.from(piece.zkProof.slice(2), 'hex')]
            }
        )

        const valid = proofResp.data;

        return valid ? { plaintext, index: guardianMap[guardian].guardianIndexPointer - 1 } : false;
    }
    catch(e) {
        console.log(e)
        return false;
    }
};

const _aggregateSharedPrivateKeys = async (signer, aesPrivateKey, blsPublicKey) => {

    const aesPublicKey = await _getAESPublicKeyfromPrivateKey(aesPrivateKey);
    const decryptionRequestResult = await _getDecryptionRequests(signer, aesPublicKey, _add0x(blsPublicKey));
    const threshold = await _getThreshold(signer);

    if (Number(decryptionRequestResult.totalNumberOfPiecesReceived) < threshold) {
        throw customErrors.INSUFFICIENT_VALID_PIECES;
    }

    const decryptionPieces = await _getPartialDecryptionPieces(
        signer,
        decryptionRequestResult.blsPublicKey,
        _add0x(aesPublicKey).toLowerCase()
    );

    if (decryptionPieces.length < threshold) {
        throw customErrors.INSUFFICIENT_VALID_PIECES;
    }

    const guardians = await _getAllGuardians(signer);
    const encryptionResult = await _getInitialEncryption(signer, blsPublicKey);

    const decryptedPieces = (await Promise.all(decryptionPieces.map(
        piece => _decryptPiece(piece, guardians, aesPrivateKey, encryptionResult))
    )).filter(piece => piece);

    if (Number(decryptedPieces.length) < threshold) {
        throw customErrors.INSUFFICIENT_VALID_PIECES;
    }

    try {
        const groupDecryptionURL = `${BASE_CRYPTOGRAPHY_WRAPPER_URL}/performGroupDecryption`;
        const groupDecryption = await axios.post(
            groupDecryptionURL,
            {
                initialCiphertext: [...Buffer.from(encryptionResult.ciphertext.slice(2), 'hex')],
                encryptorAesKey: encryptionResult.encryptorAesKey.slice(2).toUpperCase(),
                indices: decryptedPieces.map(piece => Number(piece.index)),
                plaintexts: decryptedPieces.map(piece => piece.plaintext)
            }
        );

        return groupDecryption.data;
    }
    catch(e) {
        return false;
    }
};

const _formBLSKeystore = async (blsSigningKey, password) => {
    await BLS.init();
    const { bls } = BLS;

    const publicKey = bls.secretKeyToPublicKey(
        Buffer.from(_remove0x(blsSigningKey), 'hex')
    );
    const keystore = await Keystore.create(
        password,
        Buffer.from(_remove0x(blsSigningKey), 'hex'),
        publicKey,
        'm/12381/60/0/0'
    );

    return JSON.parse(keystore.stringify());
}

/*
    @notice Internal function to fetch the decryption state in order to guide execution actions
    @param signer - Signer or provider object formed by ethers
    @param blsPublicKey - BLS public key being examined
*/
const _getDecryptionState = async (signer, blsPublicKey) => {
    const initialEncryption = await _getInitialEncryption(signer, _add0x(blsPublicKey).toLowerCase());
    if (!initialEncryption) {
        return cipStatuses.REENCRYPTION_REQUIRED
    }

    const safeBox = (await getContractInstance(signer)).safeBox();

    const isValidDecryptionRequest = await safeBox.isDecryptionRequestValid(_add0x(blsPublicKey).toLowerCase());
    if (!isValidDecryptionRequest) {
        return cipStatuses.FULLY_SECURED;
    }

    const chainID = await _extractChainID(signer);
    const { cipConstants } = _getChainSpecificConstants(chainID);

    const threshold = (await _getThreshold(signer)).toNumber();
    const latestDecryptionRequestBlock = (await safeBox.knotIdToLastRequest(_add0x(blsPublicKey))).toString();

    const lookupQuery = gql`
        query getDecryptionRequests($blsPublicKey: Bytes! $blockNumber: BigInt!) {
            decryptionRequests(first: 1, orderBy: blockNumber, orderDirection: desc
                where: { blsPublicKey: $blsPublicKey blockNumber: $blockNumber }
            ) {
                totalNumberOfPiecesReceived
            }
        }
    `;

    const response = await request(
        cipConstants.CIP_SUBGRAPH_ENDPOINT,
        lookupQuery,
        {
            blsPublicKey: _add0x(blsPublicKey),
            blockNumber: latestDecryptionRequestBlock
        }
    );

    /// Covers the scenario where status is immediately queried after submitting the decryption application
    /// This is needed to cover for delay in indexing by the graph
    if (!response || !response.decryptionRequests || response.decryptionRequests.length === 0) {
        return cipStatuses.RECOVERY_PENDING;
    }

    const { totalNumberOfPiecesReceived } = response.decryptionRequests[0];
    const decryptionCompleted = Number(totalNumberOfPiecesReceived) >= threshold;

    return decryptionCompleted ? cipStatuses.DOWNLOAD_READY : cipStatuses.RECOVERY_PENDING;
};

module.exports = {
    _generateAESCredentials,
    _formAESKeystore,
    _validateBLSKeystore,
    _unlockAESPrivateKey,
    _unlockBLSKeystore,
    _formCIPCiphertext,
    _isReEncryptionCompleted,
    _reEncrypt,
    _applyForDecryption,
    _getRequestAvailabilityBlock,
    _getPartialDecryptionPieces,
    _getAllGuardians,
    _getThreshold,
    _getAESPublicKeyfromPrivateKey,
    _aggregateSharedPrivateKeys,
    _formBLSKeystore,
    _getDecryptionState,
    _getSafeBox,
    _decryptionEligibilityChecks
};
